import React, { useState, useEffect, useRef } from 'react';
import { Pencil } from 'lucide-react';
import { Modal } from './ui/Modal';

const MAX_NAME_LENGTH = 24;

interface RenameTabModalProps {
  isOpen: boolean;
  currentTitle: string;
  existingTitles: string[];
  onClose: () => void;
  onConfirm: (newTitle: string) => void;
}

export const RenameTabModal: React.FC<RenameTabModalProps> = ({
  isOpen,
  currentTitle,
  existingTitles,
  onClose,
  onConfirm,
}) => {
  const [value, setValue] = useState(currentTitle);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setValue(currentTitle);
      setError(null);
    }
  }, [isOpen, currentTitle]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.select(), 50);
    }
  }, [isOpen]);

  const validate = (name: string): string | null => {
    const trimmed = name.trim();
    if (!trimmed) return 'Tab name cannot be empty.';
    if (trimmed.length > MAX_NAME_LENGTH) return `Tab name must be ${MAX_NAME_LENGTH} characters or fewer.`;
    const duplicates = existingTitles.filter(t => t !== currentTitle);
    if (duplicates.some(t => t.toLowerCase() === trimmed.toLowerCase())) {
      return 'A tab with this name already exists. Please choose a unique name.';
    }
    return null;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = e.target.value;
    setValue(newVal);
    setError(validate(newVal));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = value.trim();
    const validationError = validate(trimmed);
    if (validationError) {
      setError(validationError);
      return;
    }
    onConfirm(trimmed);
  };

  const isUnchanged = value.trim() === currentTitle;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 pt-2">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-100 shrink-0">
            <Pencil className="text-primary-600" size={18} />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Rename Tab</h3>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="tab-name-input" className="text-sm font-medium text-gray-700">
            Tab name
          </label>
          <input
            id="tab-name-input"
            ref={inputRef}
            type="text"
            value={value}
            onChange={handleChange}
            maxLength={MAX_NAME_LENGTH + 1}
            placeholder="Enter a name for this tab"
            className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 transition-colors ${
              error
                ? 'border-red-400 focus:ring-red-400'
                : 'border-gray-300 focus:ring-primary-500'
            }`}
          />
          <div className="flex justify-between items-center min-h-[18px]">
            {error ? (
              <p className="text-xs text-red-600">{error}</p>
            ) : (
              <span />
            )}
            <span className={`text-xs ml-auto ${value.length > MAX_NAME_LENGTH ? 'text-red-500' : 'text-gray-400'}`}>
              {value.length}/{MAX_NAME_LENGTH}
            </span>
          </div>
        </div>

        <div className="flex gap-3 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!!error || isUnchanged}
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Rename
          </button>
        </div>
      </form>
    </Modal>
  );
};
